import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { lowerLanguage } from "src/i18n";
import {
  atomIsGroup,
  atomProvinces,
  defaultValueContractorLegal,
  defaultValueContractorNatural,
  defaultValueInsured,
  selectorDocuments,
} from "src/storage";
import {
  ContractorType,
  Formik,
  FormikContractorLegal,
  FormikContractorNatural,
  FormikInsurance,
  FormikInsured,
  PeriodType,
  ViarioNationCode,
  YesNoAnswersNew,
} from "src/types";
import {
  addStartDays,
  formatDate,
  getCurrentDate,
  getCurrentDateHour,
  getCurrentMinute,
  getDifferenceInDays,
  isValidDate,
  reverseDate,
  useBoolean,
} from "src/utils";
import {
  useContractorTypeOptions,
  usePeriodOptions,
} from "../../handlers/form-label-mapping.hook";
import { MIN_PERIOD } from "src/config";
import { useGetProvinces } from "src/networking/terminals";

export const useDataEntryStep2 = (formik: Formik<FormikInsurance>) => {
  const { t } = useTranslation();

  const { contractorTypeOptions } = useContractorTypeOptions();

  const { periodOptions } = usePeriodOptions();

  const isGroup = useRecoilValue(atomIsGroup);

  const {
    values: {
      coverageStartDate,
      coverageEndDate,
      insuredPeriodType,
      insuredNumber,
      insuredList,
      contractor,
      contractorType,
    },
    errors,
    setFieldValue,
    setErrors,
    setTouched,
  } = formik;

  // Date ===========================================================
  const coverageStartDateDisabledFrom = new Date();
  const coverageEndDateDisabledFrom = new Date(
    reverseDate(
      formatDate(addStartDays(reverseDate(coverageStartDate), MIN_PERIOD)),
      "/",
      "-"
    )
  );

  const [currentDate, setCurrentDate] = useState(false);
  const [closePickerTrigger, , , toggleClosePickerTrigger] = useBoolean();

  //Set default values of start and end dates on mount
  useEffect(() => {
    //Take current start date value
    let startDate = coverageStartDate;

    if (
      !startDate ||
      errors.coverageStartDate ||
      !isValidDate(reverseDate(startDate, "/", "-"), true)
    ) {
      //override startdate with default value
      startDate = formatDate(new Date());
      setFieldValue("coverageStartDate", startDate);
    }

    if (
      !coverageEndDate ||
      errors.coverageEndDate ||
      !isValidDate(reverseDate(coverageEndDate, "/", "-"), true)
    ) {
      //Daily
      if (insuredPeriodType === PeriodType.DAILY) {
        setFieldValue("coverageEndDate", formatDate(new Date()));
        return;
      }
      //Period
      const calculatedEndDate = addStartDays(
        reverseDate(startDate),
        Number(formik.values.insuredDaysNumber) - 1
      );
      setFieldValue("coverageEndDate", formatDate(calculatedEndDate));
    }
  }, []);

  // Calculates and updates the number of insured days whenever the end date changes.
  useEffect(() => {
    const isValid = isValidDate(reverseDate(coverageEndDate, "/", "-"), true);
    if (!coverageEndDate || !isValid) return;

    // Days dif between the end and start dates
    const dateDif = getDifferenceInDays(coverageEndDate, coverageStartDate);

    // Set insuredDaysNumber to the total of days (including start and end dates)
    if (!dateDif || dateDif < 0) return;

    setFieldValue("insuredDaysNumber", dateDif + 1);
  }, [coverageEndDate]);

  useEffect(() => {
    const isValid = isValidDate(reverseDate(coverageStartDate, "/", "-"), true);
    if (!coverageStartDate || !isValid) return;

    const calculatedEndDate = addStartDays(
      reverseDate(coverageStartDate),
      Number(formik.values.insuredDaysNumber) - 1
    );
    setFieldValue("coverageEndDate", formatDate(calculatedEndDate));
  }, [coverageStartDate]);

  // Handles logic when the start date or insured period type changes.
  // Shows a warning if the policy for today with only a few hours of validity.
  useEffect(() => {
    if (!coverageStartDate) return;

    const currentDate = getCurrentDate();
    if (
      coverageStartDate &&
      currentDate &&
      insuredPeriodType === PeriodType.DAILY &&
      (getCurrentDateHour() > 15 ||
        (getCurrentDateHour() === 15 && getCurrentMinute() >= 30))
    ) {
      setCurrentDate(coverageStartDate === currentDate);
    } else {
      setCurrentDate(false);
    }
  }, [coverageStartDate, insuredPeriodType]);

  // Handles logic when the user switches the insured period type
  // Edit: coverageStartDate, coverageEndDate, insuredDaysNumber
  // Close: DatePicker
  const onSelectPeriodType = (event: ChangeEvent<HTMLInputElement>) => {
    const periodType = event?.target?.value;

    // If switching and start date is blank or has error, it set the start date to now
    if (!coverageStartDate || errors.coverageStartDate) {
      setFieldValue("coverageStartDate", formatDate(new Date()));
    }

    // If switching to "period" and dates are equal, it recalculates the end date.
    if (
      periodType === PeriodType.PERIOD &&
      coverageEndDate === coverageStartDate
    ) {
      const calculatedEndDate = addStartDays(
        reverseDate(coverageStartDate),
        Number(formik.values.insuredDaysNumber)
      );
      setFieldValue("coverageEndDate", formatDate(calculatedEndDate));
    }

    //Force
    // - insuredDaysNumber to 1
    // - endDate to startDate when switching to id_daily
    if (periodType === PeriodType.DAILY) {
      setFieldValue("insuredDaysNumber", "1");

      setFieldValue(
        "coverageEndDate",
        formatDate(
          addStartDays(
            reverseDate(coverageStartDate || formatDate(new Date())),
            0
          )
        ) //current value or default value if blank
      );
    }

    toggleClosePickerTrigger();
  };

  // Contractor Type  ===============================================
  // Handles logic when the user switches the contractor type and contractor is insured question.
  const onSelectContractorType = (event: ChangeEvent<HTMLInputElement>) => {
    const contractorType = event.target.value;
    if (!contractorType || contractorType === formik.values.contractorType)
      return;

    setFieldValue(
      "contractor",
      contractorType === ContractorType.NATURAL
        ? defaultValueContractorNatural
        : defaultValueContractorLegal
    );

    setErrors({
      ...formik.errors,
      contractor: {},
    });
    setTouched(
      {
        ...formik.touched,
        contractor: {},
      },
      false
    );
  };

  function isNaturalContractor(
    contractor: FormikContractorNatural | FormikContractorLegal
  ): contractor is FormikContractorNatural {
    return (
      (contractor as FormikContractorNatural).contractorIsInsuredQuestion !==
      undefined
    );
  }

  function isContractorNaturalAndInsured(): boolean {
    return (
      isNaturalContractor(contractor) &&
      contractor.contractorIsInsuredQuestion === YesNoAnswersNew.YES
    );
  }

  useEffect(() => {
    if (!contractor || !contractorType) return;

    const contractorIndex = insuredList.findIndex(
      (insured) => insured.id === "contractor"
    );

    if (
      isNaturalContractor(contractor) &&
      contractor.contractorIsInsuredQuestion === YesNoAnswersNew.YES
    ) {
      if (contractorIndex == -1) {
        const contractorInsured: FormikInsured = {
          id: "contractor",
          insuredName: contractor.contractorName,
          insuredSurname: contractor.contractorSurname,
          insuredBirth: contractor.contractorBirth,
          insuredBirthNation: contractor.contractorBirthNation,
          insuredBirthPlace: contractor.contractorBirthPlace,
          insuredGender: contractor.contractorGender,
          insuredNationality: contractor.contractorNationality,
          insuredFc: contractor.contractorFc,
          insuredFcQuestion: contractor.contractorFcQuestion,
        };

        const newList = [contractorInsured, ...insuredList];

        const trimmedList = newList.slice(0, Number(insuredNumber));

        setFieldValue("insuredList", trimmedList);
      }

      return;
    }

    if (contractorIndex !== -1) {
      const newList = [
        ...insuredList.slice(0, contractorIndex),
        ...insuredList.slice(contractorIndex + 1),
        defaultValueInsured,
      ];
      setFieldValue("insuredList", newList, false);
    }
  }, [
    contractorType,
    isNaturalContractor(contractor)
      ? contractor.contractorIsInsuredQuestion
      : null,
  ]);

  // Consensus  =====================================================
  const documents = useRecoilValue(selectorDocuments);

  const openInformativePersonalDataLink = () => {
    const link = documents?.find((document) => document.docCode === "1")
      ?.filePath[lowerLanguage];

    if (link) {
      window.open(link?.replaceAll('"', "").toString(), "_blank");
    }
  };

  // Viario: Provinces =============================================
  const { crossGate: getProvinces } = useGetProvinces();
  const [provinces, saveProvinces] = useRecoilState(atomProvinces);

  useEffect(() => {
    if (provinces.length > 0) return;
    getProvinces(
      {
        queryParams: {
          codiceNazione: ViarioNationCode.ITALY,
        },
      },
      {
        onSuccess(provinces) {
          saveProvinces(provinces);
        },
      }
    );
  }, []);

  return {
    periodOptions,
    coverageStartDateDisabledFrom,
    coverageEndDateDisabledFrom,
    currentDate,
    closePickerTrigger,
    contractorTypeOptions,
    isGroup,
    t,
    openInformativePersonalDataLink,
    onSelectPeriodType,
    onSelectContractorType,
    isContractorNaturalAndInsured,
  };
};
